import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import darkThemeCss from '@actual-app/components/themes/dark.css?inline';
import lightThemeCss from '@actual-app/components/themes/light.css?inline';
import midnightThemeCss from '@actual-app/components/themes/midnight.css?inline';
import nocturneThemeCss from '@actual-app/components/themes/nocturne.css?inline';
import paletteCss from '@actual-app/components/themes/palette.css?inline';
import type { DarkTheme, Theme } from '@actual-app/core/types/prefs';

import { useGlobalPref } from '#hooks/useGlobalPref';

import {
  BASE_THEME_OPTIONS,
  migrateLegacyOverride,
  parseInstalledTheme,
  validateThemeCss,
} from './customThemes';
import type { BaseTheme } from './customThemes';

const themes = {
  light: { name: 'Light', colors: lightThemeCss },
  dark: { name: 'Dark', colors: darkThemeCss },
  midnight: { name: 'Midnight', colors: midnightThemeCss },
  nocturne: { name: 'Nocturne', colors: nocturneThemeCss },
  auto: { name: 'System default', colors: darkThemeCss },
} as const;

export const themeOptions = Object.entries(themes).map(
  ([key, { name }]) => [key, name] as [Theme, string],
);

export const darkThemeOptions = Object.entries({
  dark: themes.dark,
  midnight: themes.midnight,
  nocturne: themes.nocturne,
}).map(([key, { name }]) => [key, name] as [DarkTheme, string]);

export function useTheme() {
  const [theme = 'auto', setThemePref] = useGlobalPref('theme');
  return [theme, setThemePref] as const;
}

export function usePreferredDarkTheme() {
  const [darkTheme = 'dark', setDarkTheme] =
    useGlobalPref('preferredDarkTheme');
  return [darkTheme, setDarkTheme] as const;
}

const DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function getSystemDarkModePreference() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_MODE_MEDIA_QUERY).matches
  );
}

function useSystemDarkModePreference(enabled: boolean) {
  const [isSystemDarkMode, setIsSystemDarkMode] = useState(() =>
    enabled ? getSystemDarkModePreference() : false,
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_MODE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsSystemDarkMode(event.matches);
    };

    setIsSystemDarkMode(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [enabled]);

  return isSystemDarkMode;
}

type ResolveBaseThemeOptions = {
  activeTheme: Theme;
  preferredDarkTheme: DarkTheme;
  installedCustomLightThemeJson: string | undefined;
  installedCustomDarkThemeJson: string | undefined;
  isSystemDarkMode: boolean;
};

function resolveBaseTheme({
  activeTheme,
  preferredDarkTheme,
  installedCustomLightThemeJson,
  installedCustomDarkThemeJson,
  isSystemDarkMode,
}: ResolveBaseThemeOptions): BaseTheme | undefined {
  if (activeTheme === 'auto') {
    const installedTheme = parseInstalledTheme(
      isSystemDarkMode
        ? installedCustomDarkThemeJson
        : installedCustomLightThemeJson,
    );

    return (
      installedTheme?.baseTheme ??
      (isSystemDarkMode ? preferredDarkTheme : 'light')
    );
  }

  const installedTheme = parseInstalledTheme(installedCustomLightThemeJson);
  if (installedTheme?.baseTheme) {
    return installedTheme.baseTheme;
  }

  return BASE_THEME_OPTIONS.includes(activeTheme as BaseTheme)
    ? (activeTheme as BaseTheme)
    : undefined;
}

type ResolvedThemeContextValue = {
  isDark: boolean;
  isNocturne: boolean;
};

const ResolvedThemeContext = createContext<ResolvedThemeContextValue>({
  isDark: false,
  isNocturne: false,
});

export function ResolvedThemeProvider({ children }: { children: ReactNode }) {
  const [activeTheme] = useTheme();
  const [preferredDarkTheme] = usePreferredDarkTheme();
  const [installedCustomLightThemeJson] = useGlobalPref(
    'installedCustomLightTheme',
  );
  const [installedCustomDarkThemeJson] = useGlobalPref(
    'installedCustomDarkTheme',
  );

  const isSystemDarkMode = useSystemDarkModePreference(activeTheme === 'auto');
  const baseTheme = resolveBaseTheme({
    activeTheme,
    preferredDarkTheme,
    installedCustomLightThemeJson,
    installedCustomDarkThemeJson,
    isSystemDarkMode,
  });
  const value = useMemo(
    () => ({
      isDark: baseTheme != null && baseTheme !== 'light',
      isNocturne: baseTheme === 'nocturne',
    }),
    [baseTheme],
  );

  return (
    <ResolvedThemeContext.Provider value={value}>
      {children}
    </ResolvedThemeContext.Provider>
  );
}

export function useIsDarkTheme() {
  return useContext(ResolvedThemeContext).isDark;
}

export function useIsNocturneTheme() {
  return useContext(ResolvedThemeContext).isNocturne;
}

function getBaseThemeColors(baseTheme: BaseTheme | undefined) {
  return baseTheme ? themes[baseTheme]?.colors : undefined;
}

function getResolvedBaseThemeColors(
  options: Omit<ResolveBaseThemeOptions, 'isSystemDarkMode'>,
) {
  return {
    lightColors: getBaseThemeColors(
      resolveBaseTheme({
        ...options,
        isSystemDarkMode: false,
      }),
    ),
    darkColors: getBaseThemeColors(
      resolveBaseTheme({
        ...options,
        isSystemDarkMode: true,
      }),
    ),
  };
}

function setThemeColorsFromSystemPreference(
  isSystemDarkMode: boolean,
  colors: {
    lightColors: string | undefined;
    darkColors: string | undefined;
  },
  setThemeColors: (colors: string | undefined) => void,
) {
  setThemeColors(isSystemDarkMode ? colors.darkColors : colors.lightColors);
}

/**
 * One-time migration: moves any legacy `overrideCss` field out of the
 * installed theme JSON blobs and into the new `customCssOverride` global pref.
 *
 * TODO: remove this after v26.6.0 is released
 */
function useMigrateLegacyOverride() {
  const [customCssOverride, setCustomCssOverride] =
    useGlobalPref('customCssOverride');
  const [installedCustomLightThemeJson, setInstalledCustomLightThemeJson] =
    useGlobalPref('installedCustomLightTheme');
  const [installedCustomDarkThemeJson, setInstalledCustomDarkThemeJson] =
    useGlobalPref('installedCustomDarkTheme');

  useEffect(() => {
    const result = migrateLegacyOverride({
      existingOverride: customCssOverride,
      lightJson: installedCustomLightThemeJson,
      darkJson: installedCustomDarkThemeJson,
    });

    if (!result) return;

    setCustomCssOverride(result.override);
    if (result.newLightJson !== installedCustomLightThemeJson) {
      setInstalledCustomLightThemeJson(result.newLightJson);
    }
    if (result.newDarkJson !== installedCustomDarkThemeJson) {
      setInstalledCustomDarkThemeJson(result.newDarkJson);
    }
    // Re-runs when prefs hydrate so migration isn't missed if the installed
    // theme JSONs arrive after the first render. migrateLegacyOverride is
    // idempotent: once customCssOverride is set (or the legacy field is
    // stripped), subsequent invocations return null.
  }, [
    customCssOverride,
    installedCustomLightThemeJson,
    installedCustomDarkThemeJson,
    setCustomCssOverride,
    setInstalledCustomLightThemeJson,
    setInstalledCustomDarkThemeJson,
  ]);
}

export function ThemeStyle() {
  const [activeTheme] = useTheme();
  const [darkThemePreference] = usePreferredDarkTheme();
  const [installedCustomLightThemeJson] = useGlobalPref(
    'installedCustomLightTheme',
  );
  const [installedCustomDarkThemeJson] = useGlobalPref(
    'installedCustomDarkTheme',
  );
  const [themeColors, setThemeColors] = useState<string | undefined>(undefined);

  useEffect(() => {
    const resolvedColors = getResolvedBaseThemeColors({
      activeTheme,
      preferredDarkTheme: darkThemePreference,
      installedCustomLightThemeJson,
      installedCustomDarkThemeJson,
    });

    if (activeTheme === 'auto') {
      function darkThemeMediaQueryListener(event: MediaQueryListEvent) {
        setThemeColorsFromSystemPreference(
          event.matches,
          resolvedColors,
          setThemeColors,
        );
      }
      const darkThemeMediaQuery = window.matchMedia(DARK_MODE_MEDIA_QUERY);

      darkThemeMediaQuery.addEventListener(
        'change',
        darkThemeMediaQueryListener,
      );

      setThemeColorsFromSystemPreference(
        darkThemeMediaQuery.matches,
        resolvedColors,
        setThemeColors,
      );

      return () => {
        darkThemeMediaQuery.removeEventListener(
          'change',
          darkThemeMediaQueryListener,
        );
      };
    } else {
      setThemeColors(resolvedColors.lightColors);
    }
  }, [
    activeTheme,
    darkThemePreference,
    installedCustomLightThemeJson,
    installedCustomDarkThemeJson,
  ]);

  if (!themeColors) return null;

  return (
    <>
      <style>{paletteCss}</style>
      <style>{themeColors}</style>
    </>
  );
}

/**
 * CustomThemeStyle injects CSS from the installed custom theme (if any).
 * This is rendered after ThemeStyle to allow custom themes to override base theme variables.
 *
 * When `theme === 'auto'`, separate custom themes can be set for light and dark modes,
 * injected via @media (prefers-color-scheme) rules. Otherwise, a single custom theme applies.
 */
export function CustomThemeStyle() {
  useMigrateLegacyOverride();
  const [activeTheme] = useTheme();
  const [installedCustomLightThemeJson] = useGlobalPref(
    'installedCustomLightTheme',
  );
  const [installedCustomDarkThemeJson] = useGlobalPref(
    'installedCustomDarkTheme',
  );
  const [customCssOverride] = useGlobalPref('customCssOverride');

  const validatedCss = useMemo(() => {
    const safeValidate = (css: string | undefined, errorLabel: string) => {
      if (!css?.trim()) return '';
      try {
        return validateThemeCss(css);
      } catch (error) {
        console.error(errorLabel, { error });
        return '';
      }
    };

    let baseCss = '';
    if (activeTheme === 'auto') {
      const lightCss = safeValidate(
        parseInstalledTheme(installedCustomLightThemeJson)?.cssContent,
        'Invalid custom light theme CSS',
      );
      if (lightCss) {
        baseCss += `@media (prefers-color-scheme: light) { ${lightCss} }\n`;
      }
      const darkCss = safeValidate(
        parseInstalledTheme(installedCustomDarkThemeJson)?.cssContent,
        'Invalid custom dark theme CSS',
      );
      if (darkCss) {
        baseCss += `@media (prefers-color-scheme: dark) { ${darkCss} }\n`;
      }
    } else {
      baseCss = safeValidate(
        parseInstalledTheme(installedCustomLightThemeJson)?.cssContent,
        'Invalid custom theme CSS',
      );
    }

    const overrideLayer = safeValidate(
      customCssOverride,
      'Invalid custom CSS override',
    );

    const combined = [baseCss, overrideLayer].filter(Boolean).join('\n');
    return combined || null;
  }, [
    activeTheme,
    installedCustomLightThemeJson,
    installedCustomDarkThemeJson,
    customCssOverride,
  ]);

  if (!validatedCss) {
    return null;
  }

  return <style id="custom-theme-active">{validatedCss}</style>;
}
